import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, styled, Tooltip, Typography, useTheme } from '@mui/material';
import DraggableList from 'components/DraggableList';
import { OnboardingStep } from 'further-types/lp/onboarding';
import { Column, Row } from 'further-ui/components/Layout';
import Switch from 'further-ui/components/Switch';
import { PropsWithChildren } from 'react';
import JourneyStepNumber from 'components/new/JourneyStepNumber';

const DragIndicator = styled(DragIndicatorIcon, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled: boolean }>(({ disabled }) => ({
  display: 'flex',
  cursor: disabled ? 'unset' : 'move',
}));

const Container = styled(Box)(({ theme }) => ({
  paddingBottom: 0,
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: '8px',
  backgroundColor: theme.palette.background.paper,
  width: '100%',
}));

const Main = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const Content = styled(Column)({
  maxWidth: '600px',
});

const HandleContainer = styled(Box)({
  '& div[role=button]': {
    display: 'flex',
  },
});

type Props = {
  number: number;
  title: string;
  toggleable?: boolean;
  stepKey: OnboardingStep;
  onToggle?: (stepKey: OnboardingStep) => void;
  isEnabled?: boolean;
  settings?: React.ReactNode;
  disabled?: boolean;
  disabledTooltip?: string;
  toggleTooltip?: string;
  disableDragHandle?: boolean;
};

const JourneyStep: React.FC<PropsWithChildren<Props>> = ({
  children,
  number,
  title,
  toggleable,
  onToggle,
  stepKey,
  isEnabled,
  settings,
  disabled = false,
  disabledTooltip = '',
  toggleTooltip = '',
  disableDragHandle = false,
}) => {
  const theme = useTheme();

  return (
    <Tooltip title={disabledTooltip}>
      <Container
        sx={!children ? { backgroundColor: theme.palette.grey[50] } : undefined}
      >
        <Main>
          <Row justify="space-between" fullWidth centered={!children}>
            <Content spacing="sm">
              <Row centered spacing="xs">
                <JourneyStepNumber disabled={disabled}>
                  {number}
                </JourneyStepNumber>
                <Typography
                  variant="h6"
                  color={
                    disabled ? theme.palette.grey[500] : theme.palette.grey[900]
                  }
                >
                  {title}
                </Typography>
              </Row>
              {children}
            </Content>
            <HandleContainer>
              <Row centered spacing="xs">
                {toggleable ? (
                  <Tooltip title={toggleTooltip}>
                    <div>
                      <Switch
                        checked={isEnabled}
                        onChange={() => {
                          onToggle?.(stepKey);
                        }}
                        disabled={disabled}
                      />
                    </div>
                  </Tooltip>
                ) : null}
                <DraggableList.Handle>
                  <DragIndicator
                    fontSize="small"
                    disabled={disableDragHandle || disabled}
                  />
                </DraggableList.Handle>
              </Row>
            </HandleContainer>
          </Row>
        </Main>
        {!!settings &&
        (isEnabled || stepKey === OnboardingStep.DocumentSigning) ? (
          <Box>{settings}</Box>
        ) : null}
      </Container>
    </Tooltip>
  );
};

export default JourneyStep;
